import * as React from "react";
import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/shadcn/Dialog";
import Button from "src/components/core/Button";
import { Input } from "src/components/core/Input";
import { useRenameSavedSearchMutation } from "../hooks/useRenameSavedSearchMutation";
import { SavedSearch } from "src/types/neighborhoodSearch";
import { toast } from "src/components/shadcn/useToast";

interface RenameSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  search: SavedSearch;
}

const RenameSearchDialog = ({
  open,
  onOpenChange,
  search,
}: RenameSearchDialogProps) => {
  const [newName, setNewName] = useState(search?.name || "");
  const inputRef = useRef<HTMLInputElement>(null);
  const renameMutation = useRenameSavedSearchMutation();

  // Reset the input when dialog opens/closes or search changes
  useEffect(() => {
    if (open && search) {
      setNewName(search.name);
      // Select all text when dialog opens
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.select();
        }
      }, 0);
    }
  }, [open, search]);

  const handleConfirm = async () => {
    const trimmedName = newName.trim();
    if (trimmedName && trimmedName !== search.name) {
      try {
        await renameMutation.mutateAsync({
          listId: search.id,
          newName: trimmedName,
        });

        // Show success toast
        toast({
          title: "Search renamed",
          description: (
            <>
              Successfully renamed to <strong>{trimmedName}</strong>
            </>
          ),
          variant: "success",
        });

        onOpenChange(false);
      } catch (error) {
        console.error("Failed to rename search:", error);
        // The dialog stays open on error so user can try again
      }
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleConfirm();
    }
  };

  const isValidName = newName.trim() && newName.trim() !== search?.name;
  const isLoading = renameMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-white">
        <DialogHeader>
          <DialogTitle>Rename Search</DialogTitle>
          <DialogDescription>
            Enter a new name for <strong>{search?.name}</strong>
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Input
            ref={inputRef}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Enter search name"
            onKeyDown={handleKeyDown}
            autoFocus
            disabled={isLoading}
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!isValidName || isLoading}
            isLoading={isLoading}
          >
            Rename
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { RenameSearchDialog };